import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, Box } from '@mui/material';
import { useApiContext } from '@/contexts/apiContext';
import { useTranslation } from 'react-i18next';
import ErrorMessage from '@/components/errorMessage';
import DataGridTable from '@/components/datagridTable/datagridTable';
import { GridColDef } from '@mui/x-data-grid';
import { formatDate } from 'date-fns';

type Props = { entity: any };

const llmCache: Record<string, any[]> = {};
const fetchGuard: Record<string, boolean> = {};

const EntityLlmUsages = ({ entity }: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();

  const [llmUsages, setLlmUsages] = useState<any[]>(llmCache[entity?.id] || []);
  const [loading, setLoading] = useState<boolean>(!llmCache[entity?.id]);
  const [error, setError] = useState<any>();

  const dataTimeFormat = import.meta.env.VITE_DATA_TIME_FORMAT1;

  useEffect(() => {
    const getEntityLlmUsages = async () => {
      if (!entity?.id || fetchGuard[entity.id]) return;
      fetchGuard[entity.id] = true;

      setError(null);
      if (!llmCache[entity.id]) {
        setLoading(true);
      }

      try {
        const usages = await apiContext.getEntityLlmUsages(entity.entityType, entity.entityDefId, entity.id);
        llmCache[entity.id] = usages || [];
        setLlmUsages(usages || []);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
        fetchGuard[entity.id] = false;
      }
    };

    if (entity?.id) {
      getEntityLlmUsages();
    }
  }, [entity.id, entity.entityType, entity.entityDefId]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'datetime',
        headerName: t('entity.administration.usageStats.llmUsage.columns.datatime'),
        width: 200,
        valueGetter: (value: any) => {
          return value ? formatDate(new Date(value), dataTimeFormat) : '';
        },
      },
      { field: 'userId', headerName: t('entity.administration.usageStats.llmUsage.columns.user'), flex: 1 },
      { field: 'llm', headerName: t('entity.administration.usageStats.llmUsage.columns.llm') },
      { field: 'modelName', headerName: t('entity.administration.usageStats.llmUsage.columns.modelName'), flex: 1 },
      {
        field: 'promptTokens',
        width: 150,
        headerName: t('entity.administration.usageStats.llmUsage.columns.promptTokens'),
      },
      {
        field: 'completionTokens',
        width: 150,
        headerName: t('entity.administration.usageStats.llmUsage.columns.completionTokens'),
      },
      {
        field: 'totalTokens',
        width: 150,
        headerName: t('entity.administration.usageStats.llmUsage.columns.totalTokens'),
      },
    ],
    [t, dataTimeFormat],
  );

  return (
    <Card
      variant="outlined"
      sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
    >
      <CardHeader title={t('entity.administration.usageStats.llmUsage.header')} disableTypography sx={{ pb: 0 }} />
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {error && <ErrorMessage error={error} />}
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <DataGridTable rows={llmUsages || []} columns={columns} loading={loading} />
        </Box>
      </CardContent>
    </Card>
  );
};

export default EntityLlmUsages;
